/**
 * Formulário de Qualificação de Interesse (multi-step)
 *
 * Fluxo:
 *  1. Nome
 *  2. É da Baixada Santista? — SE NÃO → bloqueia com mensagem de exclusão
 *  3. CNH categoria A ou AB definitiva? — SE NÃO → bloqueia
 *  4. WhatsApp
 *  5. Tempo pretendido (3 meses / 1 ano / conquistar)
 *  6. Já trabalha ou trabalhou como motoboy?
 *  7. Intuito da locação
 *  8. Sonha em ter uma moto?
 *  9. Qual plano interessa? (pré-selecionado via querystring)
 *  10. Envia → gera mensagem pré-preenchida no WhatsApp
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, MessageCircle, XCircle, CheckCircle2,
} from 'lucide-react';

const DESLOCA_LOGO_DARK = 'https://customer-assets.emergentagent.com/job_dcfaebc2-4990-416f-8985-7fe16afdbc64/artifacts/f24pja15_36610faa-61ce-4402-aacc-1fcef3dd6643.jpeg';

const WHATSAPP_NUM = '5513996208062';

const PLANO_LABEL = {
  '3meses': 'Plano de 3 Meses (R$ 400/semana)',
  'anual': 'Plano Anual (R$ 350/semana)',
  'fidelidade': 'Conquiste sua Moto (R$ 425/semana)',
};

const FormularioInteresse = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planoPre = searchParams.get('plano') || '';

  const [passo, setPasso] = useState(1);
  const [dados, setDados] = useState({
    nome: '',
    regiao_baixada: null,       // true/false
    cnh_valida: null,           // true/false (categoria AB ou A E definitiva)
    whatsapp: '',
    tempo_locacao: '',          // "3_meses" | "1_ano" | "conquistar"
    motoboy_experiencia: '',    // "trabalha_atualmente" | "ja_trabalhei" | "nunca"
    intuito: '',                // "renda_extra" | "delivery_app" | "locomocao" | "conquistar_moto"
    sonho_moto: '',             // "sonho_conquistar" | "so_aluguel"
    plano: planoPre,
  });
  const [bloqueado, setBloqueado] = useState(null); // { titulo, mensagem }

  const totalPassos = 9;

  const set = (k, v) => setDados(d => ({ ...d, [k]: v }));

  // Se o usuário chegou com ?plano=X, pré-seleciona
  useEffect(() => {
    if (planoPre && PLANO_LABEL[planoPre]) set('plano', planoPre);
  }, [planoPre]);

  const validarPassoAtual = () => {
    switch (passo) {
      case 1: return dados.nome.trim().length >= 2;
      case 2: {
        if (dados.regiao_baixada === false) {
          setBloqueado({
            titulo: 'Ainda não atendemos sua região',
            mensagem: 'No momento a Desloca atende apenas a Baixada Santista (Santos, São Vicente, Praia Grande, Guarujá, Cubatão, Mongaguá, Itanhaém e Peruíbe). Quando expandirmos, avisaremos você!',
          });
          return false;
        }
        return dados.regiao_baixada === true;
      }
      case 3: {
        if (dados.cnh_valida === false) {
          setBloqueado({
            titulo: 'CNH não atende aos requisitos',
            mensagem: 'Pra alugar uma moto Desloca é necessário CNH categoria A ou AB definitiva. Enquanto isso, corre pra tirar sua habilitação — quando estiver pronta, voltamos a conversar!',
          });
          return false;
        }
        return dados.cnh_valida === true;
      }
      case 4: {
        // Aceita 10-11 dígitos (BR)
        const digits = dados.whatsapp.replace(/\D/g, '');
        return digits.length >= 10 && digits.length <= 11;
      }
      case 5: return !!dados.tempo_locacao;
      case 6: return !!dados.motoboy_experiencia;
      case 7: return !!dados.intuito;
      case 8: return !!dados.sonho_moto;
      case 9: return !!dados.plano;
      default: return true;
    }
  };

  const avancar = () => {
    if (!validarPassoAtual()) return;
    if (passo < totalPassos) setPasso(p => p + 1);
    else enviarParaWhatsapp();
  };

  const voltar = () => {
    if (bloqueado) { setBloqueado(null); return; }
    if (passo > 1) setPasso(p => p - 1);
    else navigate('/');
  };

  const enviarParaWhatsapp = () => {
    // Monta mensagem legível pra Desloca
    const tempoTxt = {
      '3_meses': '3 meses',
      '1_ano': '1 ano',
      'conquistar': 'Conquistar a moto (36 meses)',
    }[dados.tempo_locacao] || '?';
    const motoboyTxt = {
      'trabalha_atualmente': 'Sim, trabalho atualmente',
      'ja_trabalhei': 'Já trabalhei antes',
      'nunca': 'Nunca trabalhei',
    }[dados.motoboy_experiencia] || '?';
    const intuitoTxt = {
      'renda_extra': 'Renda extra',
      'delivery_app': 'Delivery ou app (iFood, Uber, etc.)',
      'locomocao': 'Locomoção pessoal',
      'conquistar_moto': 'Conquistar uma moto',
    }[dados.intuito] || '?';
    const sonhoTxt = {
      'sonho_conquistar': 'Sim, sonho em conquistar minha moto',
      'so_aluguel': 'Não, só aluguel mesmo',
    }[dados.sonho_moto] || '?';
    const planoTxt = PLANO_LABEL[dados.plano] || '?';

    const msg =
`Olá Desloca! Preenchi o formulário do site.

*Nome:* ${dados.nome}
*WhatsApp:* ${dados.whatsapp}
*Região:* Baixada Santista ✅
*CNH:* Categoria A/AB definitiva ✅
*Tempo desejado:* ${tempoTxt}
*Experiência motoboy:* ${motoboyTxt}
*Intuito:* ${intuitoTxt}
*Sonho de ter uma moto:* ${sonhoTxt}
*Plano de interesse:* ${planoTxt}

Gostaria de dar continuidade na locação!`;

    const link = `https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent(msg)}`;
    window.location.href = link;
  };

  // TELA DE BLOQUEIO
  if (bloqueado) {
    return (
      <div className="min-h-screen bg-[#060A14] text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-6">
            <XCircle className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black mb-3" data-testid="bloqueio-titulo">{bloqueado.titulo}</h1>
          <p className="text-white/70 mb-8 leading-relaxed" data-testid="bloqueio-mensagem">{bloqueado.mensagem}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={voltar}
              className="px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white font-bold text-sm hover:bg-white/15 transition"
              data-testid="bloqueio-voltar"
            >
              Voltar
            </button>
            <Link
              to="/"
              className="px-6 py-3 rounded-full bg-[#00FF66] text-[#060A14] font-bold text-sm hover:brightness-110 transition"
              data-testid="bloqueio-home"
            >
              Ir para o início
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const podeAvancar = validarPassoAtual();

  return (
    <div className="min-h-screen bg-[#060A14] text-white flex flex-col">
      {/* Header simplificado */}
      <header className="border-b border-white/5 px-4 sm:px-6 py-3 flex items-center gap-3">
        <button
          onClick={voltar}
          className="p-2 rounded-lg hover:bg-white/5 text-white"
          aria-label="Voltar"
          data-testid="form-voltar"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Link to="/" className="flex items-center">
          <img
            src={DESLOCA_LOGO_DARK}
            alt="Desloca"
            className="h-11 w-auto object-contain"
          />
        </Link>
        <div className="ml-auto text-xs text-white/50" data-testid="form-progresso">
          {passo}/{totalPassos}
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-white/5">
        <div
          className="h-1 bg-[#00FF66] transition-all"
          style={{ width: `${(passo / totalPassos) * 100}%` }}
        />
      </div>

      {/* CONTEÚDO */}
      <main className="flex-1 flex items-start sm:items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-lg" key={passo}>
          {passo === 1 && (
            <StepText
              titulo="Qual é o seu nome?"
              subtitulo="Vamos começar com o básico."
              value={dados.nome}
              onChange={v => set('nome', v)}
              placeholder="Ex: João da Silva"
              testId="input-nome"
            />
          )}

          {passo === 2 && (
            <StepEscolha
              titulo="Você mora na Baixada Santista?"
              subtitulo="Atendemos Santos, São Vicente, Praia Grande, Guarujá, Cubatão, Mongaguá, Itanhaém e Peruíbe."
              opcoes={[
                { valor: true, label: 'Sim, moro na Baixada Santista', testId: 'regiao-sim' },
                { valor: false, label: 'Não, moro em outra região', testId: 'regiao-nao' },
              ]}
              value={dados.regiao_baixada}
              onChange={v => { set('regiao_baixada', v); if (v === true) setTimeout(() => setPasso(3), 200); }}
            />
          )}

          {passo === 3 && (
            <StepEscolha
              titulo="Você tem CNH categoria A ou AB, definitiva?"
              subtitulo="Não pode ser permissão provisória — precisa ser a CNH definitiva."
              opcoes={[
                { valor: true, label: 'Sim, tenho CNH A ou AB definitiva', testId: 'cnh-sim' },
                { valor: false, label: 'Não tenho ou é permissão provisória', testId: 'cnh-nao' },
              ]}
              value={dados.cnh_valida}
              onChange={v => { set('cnh_valida', v); if (v === true) setTimeout(() => setPasso(4), 200); }}
            />
          )}

          {passo === 4 && (
            <StepText
              titulo="Qual seu WhatsApp?"
              subtitulo="Vamos usar esse número pra dar continuidade ao seu atendimento."
              value={dados.whatsapp}
              onChange={v => set('whatsapp', mascaraTelefone(v))}
              placeholder="(13) 99620-8062"
              type="tel"
              testId="input-whatsapp"
            />
          )}

          {passo === 5 && (
            <StepEscolha
              titulo="Por quanto tempo você pretende alugar?"
              opcoes={[
                { valor: '3_meses', label: '3 meses', testId: 'tempo-3meses' },
                { valor: '1_ano', label: '1 ano', testId: 'tempo-1ano' },
                { valor: 'conquistar', label: 'Quero conquistar a moto (36 meses)', testId: 'tempo-conquistar' },
              ]}
              value={dados.tempo_locacao}
              onChange={v => { set('tempo_locacao', v); setTimeout(() => setPasso(6), 200); }}
            />
          )}

          {passo === 6 && (
            <StepEscolha
              titulo="Você trabalha ou já trabalhou como motoboy?"
              subtitulo="Só pra entender melhor seu perfil — não é obrigatório."
              opcoes={[
                { valor: 'trabalha_atualmente', label: 'Sim, trabalho atualmente', testId: 'motoboy-atual' },
                { valor: 'ja_trabalhei', label: 'Já trabalhei antes', testId: 'motoboy-passado' },
                { valor: 'nunca', label: 'Nunca trabalhei como motoboy', testId: 'motoboy-nunca' },
              ]}
              value={dados.motoboy_experiencia}
              onChange={v => { set('motoboy_experiencia', v); setTimeout(() => setPasso(7), 200); }}
            />
          )}

          {passo === 7 && (
            <StepEscolha
              titulo="Qual o intuito da locação?"
              opcoes={[
                { valor: 'renda_extra', label: 'Renda extra', testId: 'intuito-renda' },
                { valor: 'delivery_app', label: 'Delivery ou app (iFood, Uber, etc.)', testId: 'intuito-delivery' },
                { valor: 'locomocao', label: 'Locomoção pessoal', testId: 'intuito-locomocao' },
                { valor: 'conquistar_moto', label: 'Conquistar uma moto', testId: 'intuito-conquistar' },
              ]}
              value={dados.intuito}
              onChange={v => { set('intuito', v); setTimeout(() => setPasso(8), 200); }}
            />
          )}

          {passo === 8 && (
            <StepEscolha
              titulo="Você sonha em ter sua própria moto?"
              opcoes={[
                { valor: 'sonho_conquistar', label: 'Sim, sonho em conquistar minha moto', testId: 'sonho-sim' },
                { valor: 'so_aluguel', label: 'Não, só quero aluguel mesmo', testId: 'sonho-nao' },
              ]}
              value={dados.sonho_moto}
              onChange={v => { set('sonho_moto', v); setTimeout(() => setPasso(9), 200); }}
            />
          )}

          {passo === 9 && (
            <StepEscolha
              titulo="Qual plano te interessou mais?"
              subtitulo="Você poderá conversar com nosso atendimento pra ajustar."
              opcoes={[
                { valor: '3meses', label: PLANO_LABEL['3meses'], testId: 'plano-3meses' },
                { valor: 'anual', label: PLANO_LABEL['anual'], testId: 'plano-anual' },
                { valor: 'fidelidade', label: PLANO_LABEL['fidelidade'], testId: 'plano-fidelidade' },
              ]}
              value={dados.plano}
              onChange={v => set('plano', v)}
            />
          )}
        </div>
      </main>

      {/* CTA de avançar (fixado embaixo em mobile) */}
      <footer
        className="border-t border-white/5 bg-[#060A14] px-4 py-4 sticky bottom-0"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={avancar}
          disabled={!podeAvancar}
          className={`w-full max-w-lg mx-auto flex items-center justify-center gap-2 py-4 rounded-full font-bold text-base transition ${
            podeAvancar
              ? 'bg-[#00FF66] text-[#060A14] hover:brightness-110 shadow-lg shadow-[#00FF66]/25'
              : 'bg-white/10 text-white/40 cursor-not-allowed'
          }`}
          data-testid="form-avancar"
        >
          {passo === totalPassos ? (
            <>Enviar e falar no WhatsApp <MessageCircle className="w-4 h-4" /></>
          ) : (
            <>Continuar <ArrowRight className="w-4 h-4" /></>
          )}
        </button>
      </footer>
    </div>
  );
};

const StepText = ({ titulo, subtitulo, value, onChange, placeholder, type = 'text', testId }) => (
  <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
    <h2 className="text-2xl sm:text-3xl font-black mb-2">{titulo}</h2>
    {subtitulo && <p className="text-white/60 mb-6 text-sm">{subtitulo}</p>}
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white/[0.03] border-2 border-white/10 focus:border-[#00FF66] rounded-2xl px-4 py-4 text-base text-white placeholder-white/30 outline-none transition"
      data-testid={testId}
      autoFocus
    />
  </div>
);

const StepEscolha = ({ titulo, subtitulo, opcoes, value, onChange }) => (
  <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
    <h2 className="text-2xl sm:text-3xl font-black mb-2">{titulo}</h2>
    {subtitulo && <p className="text-white/60 mb-6 text-sm">{subtitulo}</p>}
    <div className="space-y-3">
      {opcoes.map((op) => {
        const selecionado = value === op.valor;
        return (
          <button
            key={String(op.valor)}
            onClick={() => onChange(op.valor)}
            className={`w-full text-left px-4 py-4 rounded-2xl border-2 transition flex items-center gap-3 ${
              selecionado
                ? 'border-[#00FF66] bg-[#00FF66]/10 text-white'
                : 'border-white/10 bg-white/[0.03] text-white/80 hover:border-white/25'
            }`}
            data-testid={op.testId}
          >
            <div className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center transition ${
              selecionado ? 'border-[#00FF66] bg-[#00FF66]' : 'border-white/30'
            }`}>
              {selecionado && <CheckCircle2 className="w-4 h-4 text-[#060A14]" />}
            </div>
            <span className="text-sm sm:text-base font-medium">{op.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const mascaraTelefone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0,2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
};

export default FormularioInteresse;
